<template>
  <div class="faq">
    <div class="faq-hero">
      <div>
        <h1 class="faq-title">Часто задаваемые вопросы</h1>
        <p class="faq-subtitle">
          Ответы на типовые вопросы по работе в AVR+. Выберите свою роль или найдите вопрос по ключевому слову.
          Подробные пошаговые инструкции — в разделе
          <router-link :to="{name: 'tutorial'}" class="custom-link">«Обучение»</router-link>.
        </p>
      </div>
      <div class="faq-search">
        <i class="material-icons">search</i>
        <input v-model.trim="search" type="text" placeholder="Поиск: например, «пароль», «NCALayer», «Excel», «заголовок»">
        <button v-if="search" type="button" title="Очистить" @click="search = ''">
          <i class="material-icons">close</i>
        </button>
      </div>
    </div>

    <div class="faq-tabs">
      <button type="button" class="faq-tab" :class="{active: activeGroup === ''}" @click="selectGroup('')">
        Все вопросы <small>{{ items.length }}</small>
      </button>
      <button v-for="group in groups" :key="group.code" type="button" class="faq-tab"
              :class="{active: activeGroup === group.code, mine: myGroups.includes(group.code)}"
              @click="selectGroup(group.code)">
        {{ group.title }} <small>{{ countByGroup(group.code) }}</small>
      </button>
    </div>

    <div v-if="!filtered.length" class="faq-empty">
      По запросу «{{ search }}» ничего не найдено. Попробуйте другое слово, откройте
      <router-link :to="{name: 'tutorial'}" class="custom-link">«Обучение»</router-link>
      или напишите нам через <router-link :to="{name: 'offers'}" class="custom-link">«Предложения»</router-link>.
    </div>

    <div v-for="group in groupedFiltered" :key="group.code" class="faq-group">
      <h2 class="faq-group-title">{{ group.title }}</h2>
      <div v-for="item in group.items" :key="item.key" class="faq-item" :class="{open: opened[item.key]}">
        <button type="button" class="faq-question" @click="toggle(item.key)">
          <span v-html="highlight(item.q)"></span>
          <i class="material-icons">{{ opened[item.key] ? 'expand_less' : 'expand_more' }}</i>
        </button>
        <div v-show="opened[item.key]" class="faq-answer">
          <p v-for="(paragraph, i) in item.a.split('\n')" :key="i" v-html="highlight(paragraph)"></p>
        </div>
      </div>
    </div>

    <div class="faq-support">
      <div>
        <div class="faq-support-title">Не нашли ответ?</div>
        <div class="faq-support-text">
          Напишите через раздел <router-link :to="{name: 'offers'}" class="custom-link">«Предложения»</router-link>
          или в поддержку: <a :href="support.phoneHref" class="custom-link">{{ support.phone }}</a>,
          <a :href="'mailto:' + support.email" class="custom-link">{{ support.email }}</a>.
        </div>
      </div>
      <router-link :to="{name: 'tutorial'}" class="faq-support-btn">
        <i class="material-icons">school</i> Открыть обучение
      </router-link>
    </div>
  </div>
</template>

<script>
import {FAQ_GROUPS, FAQ_ITEMS} from '@/data/faq';
import {SUPPORT} from '@/data/tutorial';

export default {
  name: 'Learning',
  data() {
    return {
      groups: FAQ_GROUPS,
      staticItems: FAQ_ITEMS,
      serverItems: [],
      support: SUPPORT,
      activeGroup: '',
      myGroups: [],
      search: '',
      opened: {},
    };
  },
  computed: {
    // встроенные вопросы + вопросы, заведённые в админке (core.FAQ), без дублей по тексту вопроса
    items() {
      let seen = {};
      let result = [];
      this.staticItems.concat(this.serverItems).forEach((item, index) => {
        let key = (item.group + '|' + item.q).toLowerCase();
        if (!seen[key]) {
          seen[key] = true;
          result.push(Object.assign({key: 'faq-' + index}, item));
        }
      });
      return result;
    },
    filtered() {
      let query = this.search.toLowerCase();
      return this.items.filter(item => {
        if (query === '') {
          return this.activeGroup === '' || item.group === this.activeGroup;
        }
        return (item.q + ' ' + item.a).toLowerCase().includes(query);
      });
    },
    groupedFiltered() {
      return this.groups
          .map(group => ({
            code: group.code,
            title: group.title,
            items: this.filtered.filter(item => item.group === group.code),
          }))
          .filter(group => group.items.length);
    },
  },
  created() {
    this.detectRoles();
    this.$store.dispatch('getAllFAQ').then((response) => {
      let data = Array.isArray(response.data) ? response.data : (response.data.results || []);
      this.serverItems = data.map(faq => ({
        group: this.groups.some(g => g.code === faq.role_for) ? faq.role_for : 'Общее',
        q: faq.q_name,
        a: faq.content || '',
      }));
    }).catch(() => {});
  },
  methods: {
    detectRoles() {
      let roleIds = [];
      try {
        roleIds = JSON.parse(localStorage.getItem('roles') || '[]');
      } catch (e) {
        roleIds = [];
      }
      if (!Array.isArray(roleIds)) {
        roleIds = String(roleIds).split(',').map(Number);
      }
      this.myGroups = this.groups
          .filter(group => group.roleIds.some(id => roleIds.includes(id)))
          .map(group => group.code);
      this.activeGroup = this.myGroups[0] || '';
    },
    selectGroup(code) {
      this.activeGroup = code;
      this.search = '';
    },
    countByGroup(code) {
      return this.items.filter(item => item.group === code).length;
    },
    toggle(key) {
      this.$set(this.opened, key, !this.opened[key]);
    },
    highlight(text) {
      let query = this.search;
      if (!query || query.length < 2) {
        return text;
      }
      let escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      return text.replace(new RegExp('(' + escaped + ')', 'gi'), '<mark>$1</mark>');
    },
  },
};
</script>

<style scoped>
.faq {
  width: 92%;
  max-width: 1100px;
  margin: 2rem auto 4rem auto;
  color: #1f2937;
}

.faq-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2rem;
  flex-wrap: wrap;
  margin-bottom: 1.5rem;
}

.faq-title {
  font-size: 2rem;
  font-weight: 700;
  color: #23468c;
  margin: 0 0 0.5rem 0;
}

.faq-subtitle {
  max-width: 640px;
  color: #4b5563;
  line-height: 1.5;
}

.faq-search {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 10px;
  padding: 0.5rem 0.75rem;
  min-width: 320px;
  flex: 1;
  max-width: 440px;
}

.faq-search input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 0.95rem;
}

.faq-search button {
  border: none;
  background: none;
  cursor: pointer;
  color: #6b7280;
  display: flex;
}

.faq-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
}

.faq-tab {
  padding: 0.5rem 0.9rem;
  border-radius: 999px;
  border: 1px solid #d1d5db;
  background: white;
  cursor: pointer;
  font-size: 0.9rem;
}

.faq-tab small {
  margin-left: 0.3rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: #f3f4f6;
  font-size: 0.75rem;
}

.faq-tab.mine {
  border-color: #23468c;
}

.faq-tab.active {
  background: #23468c;
  color: white;
  border-color: #23468c;
}

.faq-tab.active small {
  background: rgba(255, 255, 255, 0.25);
}

.faq-empty {
  background: white;
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.faq-group {
  margin-bottom: 1.5rem;
}

.faq-group-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #111827;
  margin: 0 0 0.5rem 0.25rem;
}

.faq-item {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  margin-bottom: 0.5rem;
  overflow: hidden;
}

.faq-item.open {
  border-color: #23468c;
}

.faq-question {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  text-align: left;
  padding: 0.9rem 1.1rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  font-weight: 600;
  color: #1f2937;
}

.faq-question:hover {
  background: #f8fafc;
}

.faq-answer {
  padding: 0 1.1rem 1rem 1.1rem;
  color: #374151;
  line-height: 1.6;
}

.faq-answer p + p {
  margin-top: 0.5rem;
}

.faq >>> mark {
  background: #fde68a;
  padding: 0 0.1rem;
}

.faq-support {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  background: #e8eefb;
  border-radius: 12px;
  padding: 1.25rem 1.5rem;
  margin-top: 2rem;
}

.faq-support-title {
  font-weight: 700;
  font-size: 1.1rem;
  color: #23468c;
}

.faq-support-text {
  color: #374151;
  margin-top: 0.25rem;
}

.faq-support-btn {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: #23468c;
  color: white;
  padding: 0.7rem 1.2rem;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
}
</style>
